import { characterList2PresentationStart, createCharacterList2Presentation } from '../../lib/character_list_2_presentation.js';
import { Controller } from '@hotwired/stimulus';
import { waitForRoundAnimation } from '../../lib/round_animation.js';

const PHASE_ATTRIBUTE = 'data-character-list-2-reveal-phase';

// A presentation-only copy flies above the screen while the real, morphable card
// stays in the roster. Only an existing hidden card becoming active triggers it.
export default class extends Controller {
  static targets = ['presentations'];

  connect() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.cancelOnMotionChange = () => this.cancelAndCenter();
    this.reducedMotion.addEventListener('change', this.cancelOnMotionChange);
  }

  beforeMorph(event) {
    if (this.element?.getAttribute('data-game-screen--character-list-2-layout-combat-value') === 'false') return;
    const character = event.target;
    const incoming = event.detail.newElement;
    if (!incoming) {
      if (this.reveal && character.contains(this.reveal.character)) this.cancel();
      return;
    }
    if (!character.matches('[data-character-list-2-card]')) return;

    const becomingActive = incoming.classList.contains('is-active') &&
      !incoming.classList.contains('is-hidden');

    if (this.reveal?.character === character) {
      if (!becomingActive) this.cancel();
      return;
    }

    if (!becomingActive || !character.classList.contains('is-hidden') || this.reducedMotion.matches) return;

    this.cancel();
    this.reveal = { character, timers: new Map(), animations: new Set() };
    character.setAttribute(PHASE_ATTRIBUTE, 'waiting');
  }

  preservePhase(event) {
    if (event.target === this.reveal?.character && event.detail.attributeName === PHASE_ATTRIBUTE) {
      event.preventDefault();
    }
  }

  afterMorph(event) {
    const reveal = this.reveal;
    if (event.target !== reveal?.character || reveal.started) return;
    reveal.started = true;
    // Finish the enclosing Turbo morph before adding the temporary presentation.
    queueMicrotask(() => {
      if (this.reveal === reveal) this.play(reveal);
    });
  }

  async play(reveal) {
    try {
      await waitForRoundAnimation(reveal.character);
      if (this.reveal !== reveal || !reveal.character.isConnected) return;
      await this.reserveSpace(reveal);
      if (this.reveal !== reveal) return;
      this.dispatch('before-presentation', { detail: { character: reveal.character } });
      reveal.overlay = this.createOverlay(reveal.character);
      await this.prepareEntrance(reveal);
      if (this.reveal !== reveal) return;
      await this.animate(reveal, [
        characterList2PresentationStart,
        { transform: 'translate(-50%, -50%) rotate(0deg)', opacity: 1 }
      ], 650);
      if (this.reveal !== reveal) return;

      await this.wait(reveal, 3000);
      if (this.reveal !== reveal) return;
      await this.centerForLanding(reveal);
      if (this.reveal !== reveal) return;
      const card = reveal.character.querySelector('.character-list-2__card');
      const destination = card.getBoundingClientRect();
      const origin = reveal.overlay.getBoundingClientRect();
      if (!destination.width || !destination.height) return;

      // Bounding rectangles include screen rotation; rotate the flying card to
      // match the destination before handing back to the real card.
      const rotation = this.rotation(card);
      const quarterTurn = Math.abs(rotation % 180) === 90;
      const width = quarterTurn ? destination.height : destination.width;
      const height = quarterTurn ? destination.width : destination.height;
      const x = destination.left + destination.width / 2 - window.innerWidth / 2;
      const y = destination.top + destination.height / 2 - window.innerHeight / 2;
      reveal.overlay.classList.add('is-landing');
      await this.animate(reveal, [
        { transform: 'translate(-50%, -50%) rotate(0deg) scale(1)' },
        { transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) rotate(${rotation}deg) scale(${width / origin.width}, ${height / origin.height})` }
      ], 800);
      if (this.reveal === reveal) {
        reveal.landed = true;
        this.dispatch('before-landing-complete', { detail: { character: reveal.character } });
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Character reveal failed', error);
    } finally {
      if (this.reveal === reveal) this.cancel();
    }
  }

  createOverlay(character) {
    return createCharacterList2Presentation(character, this.presentationsTarget);
  }

  async reserveSpace(reveal) {
    this.dispatch('space-start', { detail: { character: reveal.character } });
    reveal.character.setAttribute(PHASE_ATTRIBUTE, 'space');
    // The reveal phase reserves only the portrait footprint. The normal turn
    // sequence opens the status panel once the flying card has landed.
    const space = reveal.character.animate([
      { maxWidth: '0px' },
      { maxWidth: `${reveal.character.offsetWidth}px` }
    ], { duration: 350, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' });
    reveal.animations.add(space);
    await space.finished;
    if (this.reveal !== reveal) return;
    reveal.animations.delete(space);
    space.cancel();
    this.dispatch('space-grown', { detail: { character: reveal.character } });
  }

  prepareEntrance(reveal) {
    return new Promise(resolve => {
      let frame;
      const finish = () => {
        cancelAnimationFrame(frame);
        delete reveal.finishEntrance;
        resolve();
      };
      reveal.finishEntrance = finish;
      // Commit the offscreen pose and let insertion observers finish before
      // starting the clock, so that setup work cannot consume the entrance.
      frame = requestAnimationFrame(() => { frame = requestAnimationFrame(finish); });
    });
  }

  rotation(character) {
    let rotation = 0;
    for (let element = character; element; element = element.parentElement) {
      const transform = getComputedStyle(element).transform;
      if (transform === 'none') continue;
      const matrix = new DOMMatrixReadOnly(transform);
      rotation += Math.atan2(matrix.b, matrix.a) * 180 / Math.PI;
    }
    return Math.round(rotation);
  }

  animate(reveal, keyframes, duration) {
    const animation = reveal.overlay.animate(keyframes, {
      duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both'
    });
    reveal.animations.add(animation);
    return animation.finished;
  }

  centerForLanding(reveal) {
    const viewport = this.element.querySelector('.character-list-2__viewport');
    return new Promise(resolve => {
      const initialLeft = viewport.scrollLeft;
      let frame;
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        cancelAnimationFrame(frame);
        viewport.removeEventListener('scrollend', ended);
        delete reveal.finishCentering;
        resolve();
      };
      const ended = event => { if (event.target === viewport) finish(); };
      reveal.finishCentering = finish;
      // Native smooth scrolling can outlast a fixed presentation delay. Listen
      // before dispatching so even an immediate scroll completion is captured.
      viewport.addEventListener('scrollend', ended);
      const timer = setTimeout(finish, 2000);
      this.dispatch('center', { detail: { character: reveal.character, behavior: 'smooth' } });
      if (finished) return;
      const checkUnchanged = remaining => {
        if (Math.abs(viewport.scrollLeft - initialLeft) > 0.5) return;
        if (remaining === 0) return finish();
        frame = requestAnimationFrame(() => checkUnchanged(remaining - 1));
      };
      // Already-centered and overflow-visible focus views do not emit scrollend.
      // Let the browser begin any requested smooth scroll before taking that path.
      frame = requestAnimationFrame(() => checkUnchanged(2));
    });
  }

  wait(reveal, duration) {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        reveal.timers.delete(timer);
        resolve();
      }, duration);
      reveal.timers.set(timer, resolve);
    });
  }

  cancelAndCenter() {
    const character = this.reveal?.character;
    this.cancel();
    if (character?.isConnected && character.classList.contains('is-active') &&
        !character.classList.contains('is-hidden')) {
      this.dispatch('center', { detail: { character, behavior: 'instant' } });
    }
  }

  cancel() {
    const reveal = this.reveal;
    if (!reveal) return;
    this.reveal = null;
    reveal.finishEntrance?.();
    reveal.finishCentering?.();
    for (const [timer, resolve] of reveal.timers) {
      clearTimeout(timer);
      resolve();
    }
    for (const animation of reveal.animations) animation.cancel();
    reveal.overlay?.remove();
    reveal.character.removeAttribute(PHASE_ATTRIBUTE);
    this.dispatch('finished', { detail: { character: reveal.character, landed: reveal.landed === true } });
  }

  disconnect() {
    this.cancel();
    this.reducedMotion.removeEventListener('change', this.cancelOnMotionChange);
  }
}
